import { useStore } from '@nanostores/react'
import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'

import type { Filter, MailboxFilter } from '../model/types'

import { $filter, $mailboxFilter, $search, $searchDraft, useFilter } from './useFilters'

const FILTER_PARAM = 'filter'
const CHIP_FILTER_PARAM = 'chip-filter'
const SEARCH_PARAM = 'search'

const allowedFilters: ReadonlySet<Filter> = new Set<Filter>([
  'archive',
  'focused',
  'other',
  'primary',
  'spam',
])

const allowedChipFilters: ReadonlySet<Exclude<MailboxFilter, ''>> = new Set<
  Exclude<MailboxFilter, ''>
>(['in-mail', 'myConnections', 'starred', 'unread'])

export function useFiltersQuerySync() {
  const [params, setParams] = useSearchParams()

  const filter = useFilter()

  useEffect(() => {
    const urlFilter = params.get(FILTER_PARAM) as Filter | null
    const urlChip = params.get(CHIP_FILTER_PARAM) as MailboxFilter | null
    const urlSearch = params.get(SEARCH_PARAM) ?? ''

    if (urlFilter && allowedFilters.has(urlFilter)) {
      $filter.set(urlFilter)
    }

    if (urlChip && allowedChipFilters.has(urlChip as Exclude<MailboxFilter, ''>)) {
      $mailboxFilter.set(urlChip as MailboxFilter)
    }

		$search.set(urlSearch)

  }, [params])

  const currentMailbox = useStore($mailboxFilter)
  const currentSearch = useStore($search)

  useEffect(() => {
    setParams(prev => {
      if (currentSearch) {
        prev.set(SEARCH_PARAM, currentSearch)
        prev.delete(FILTER_PARAM)
        prev.delete(CHIP_FILTER_PARAM)
      } else {
        if (filter && allowedFilters.has(filter)) {
          prev.set(FILTER_PARAM, filter)
        } else {
          prev.delete(FILTER_PARAM)
        }

        if (currentMailbox && allowedChipFilters.has(currentMailbox)) {
          prev.set(CHIP_FILTER_PARAM, currentMailbox)
        } else {
          prev.delete(CHIP_FILTER_PARAM)
        }

        prev.delete(SEARCH_PARAM)
      }

      return prev
    })
  }, [filter, currentMailbox, currentSearch, setParams])

  useEffect(() => {
    return () => {
      setParams(prev => {
        prev.delete(FILTER_PARAM)
        prev.delete(CHIP_FILTER_PARAM)
        prev.delete(SEARCH_PARAM)
        return prev
      })
      $filter.set('')
      $mailboxFilter.set('')
      $search.set('')
			$searchDraft.set('')
    }
  }, [])
}


